import {
  constructList,
  getCategories,
  getFacet,
  getText,
  sortAndSliceList,
  appendGOATandCountryCodeToTitle,
  stripSpecialCharacterFromString,
} from 'shared/utils/pageMetadataUtils';
import { startCase } from 'shared/utils/startCase';
import { t } from 'localization/utils/t';

const TP = 'shared.utils.getColorValueMetadata';

export const getColorValueMetadata = ({
  pageTitle,
  pageDescription,
  constructorResponse,
  locale,
  query,
}) => {
  const { groups = [], facets = [], results = [] } = constructorResponse?.response || {};
  const colorValue: string = query?.colorValue;
  const colorValueWithStartCase: string = startCase(colorValue);
  const topLevelCategory: string =
    groups?.[0]?.group_id === 'sneakers'
      ? t(`${TP}.shoes`, 'shoes')
      : stripSpecialCharacterFromString(groups?.[0]?.group_id);
  const topLevelCategoryWithStartCase: string = startCase(topLevelCategory);

  const { subCategories } = getCategories(groups);

  const brandFacet = getFacet(facets, 'brand');
  const categoryFacet = getFacet(facets, 'web_groups');

  const topTwoBrands = constructList({
    list: sortAndSliceList(brandFacet?.options, 2),
    key: 'display_name',
  });
  const topThreeBrands = constructList({
    list: sortAndSliceList(brandFacet?.options),
    key: 'display_name',
  });
  const numOfProducts: number = categoryFacet?.options.find(
    (option) => option.value === query?.pageSlug,
  )?.count;

  const topTwoPTs = constructList({ list: sortAndSliceList(results, 2), key: 'value' });

  // Pulling out static strings for translation purposes
  const more = t(`${TP}.more`, 'more');

  const _topThreeBrands = topThreeBrands?.constructedListWithStartCase;
  if (_topThreeBrands?.length > 1) {
    _topThreeBrands[_topThreeBrands?.length - 1] = `${more}`;
  }

  if (query?.pageSlug === 'sneakers') {
    const colonWithTopThreeBrands = getText(':', _topThreeBrands);
    const formattedTopTwoBrands = getText('', topTwoBrands.constructedList);
    const iconicStylesFromTopTwoBrands =
      formattedTopTwoBrands?.length > 0
        ? t(`${TP}.iconicStylesFromTopTwoBrands`, 'iconic styles from {formattedTopTwoBrands}', {
            formattedTopTwoBrands,
          })
        : '';
    const formattedTopTwoPTs = getText('', topTwoPTs.constructedListWithStartCase);
    const exploreUpcomingAndRecentReleasesLikeTopTwoPTs =
      formattedTopTwoPTs?.length > 0
        ? t(
            `${TP}.exploreUpcomingAndRecentReleasesLikeTopTwoPTs`,
            'Explore upcoming and recent releases like {formattedTopTwoPTs}',
            { formattedTopTwoPTs },
          )
        : '';

    const title = t(
      `${TP}.title`,
      'Buy {colorValueWithStartCase} {topLevelCategoryWithStartCase}{colonWithTopThreeBrands}',
      {
        colorValueWithStartCase,
        topLevelCategoryWithStartCase,
        colonWithTopThreeBrands,
      },
    );
    const description = t(
      `${TP}.description`,
      'Shop {colorValue} {topLevelCategory} on GOAT including {numOfProducts} {iconicStylesFromTopTwoBrands}. {exploreUpcomingAndRecentReleasesLikeTopTwoPTs}',
      {
        colorValue,
        topLevelCategory,
        numOfProducts,
        iconicStylesFromTopTwoBrands,
        exploreUpcomingAndRecentReleasesLikeTopTwoPTs,
      },
    );

    return {
      pageTitle: pageTitle || appendGOATandCountryCodeToTitle(title, locale),
      pageDescription: pageDescription || description,
    };
  }

  const _subCategories = subCategories?.constructedList;
  if (_subCategories?.length > 1) {
    _subCategories[_subCategories?.length - 1] = `${more}`;
  }

  const colonWithTopThreeSubCategories: string = startCase(getText(':', _subCategories));

  const formattedTopThreeBrands = getText('', _topThreeBrands);
  const stylesFromTopThreeBrands =
    formattedTopThreeBrands?.length > 0
      ? t(`${TP}.stylesFromTopThreeBrands`, 'styles from {topThreeBrands}', { topThreeBrands })
      : '';

  const topThreeSubCats = getText('', _subCategories);
  const exploreNewReleasesAndIconicStylesAcrossTopThreeSubCats =
    topThreeSubCats?.length > 0
      ? t(
          `${TP}.exploreNewReleasesAndIconicStylesAcross`,
          'Explore new releases & iconic styles across {topThreeSubCats}',
          { topThreeSubCats },
        )
      : '';

  const title = t(
    `${TP}.title2`,
    'Buy {colorValueWithStartCase} {topLevelCategoryWithStartCase}{colonWithTopThreeSubCategories}',
    {
      colorValueWithStartCase,
      topLevelCategoryWithStartCase,
      colonWithTopThreeSubCategories,
    },
  );
  const description = t(
    `${TP}.description2`,
    'Shop {colorValue} {topLevelCategory} on GOAT. {numOfProducts} {stylesFromTopThreeBrands}. {exploreNewReleasesAndIconicStylesAcrossTopThreeSubCats}. Authenticity assured.',
    {
      colorValue,
      topLevelCategory,
      numOfProducts,
      stylesFromTopThreeBrands,
      exploreNewReleasesAndIconicStylesAcrossTopThreeSubCats,
    },
  );

  return {
    pageTitle: pageTitle || appendGOATandCountryCodeToTitle(startCase(title), locale),
    pageDescription: pageDescription || description,
  };
};
